import { useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { toast } from 'react-hot-toast';
import { useQueryClient } from '@tanstack/react-query';
import {
  useGetSellerCatalogPrices,
  useSetSellerProductPrice,
} from '~/api/my-products';
import { useGetUserPreferences, useUpdateUserPreferences } from '~/api';
import { Button } from '~/components/ui';
import { BuyerMarginsModal } from '~/pages/my-products-edit/BuyerMarginsModal';
import {
  applyColumnOrder,
  Table,
  useColumnOrderPreference,
  useTable,
  type TableColumn,
  type TableRow,
} from '~/components/ui/table';
import { usePaginatedQuery } from '~/hooks/usePaginatedQuery';
import { useDebounce } from '~/hooks/useDebounce';
import { buildAndFilter } from '~/types';
import {
  filterRowsToClauses,
  searchTermClause,
} from '~/pages/my-products/lib/filterClauses';
import { MY_PRODUCTS_FILTER_VIEWS } from '~/pages/my-products/lib/filterViews';
import {
  categoryOf,
  withoutCategory,
} from '~/pages/my-products/lib/myProductsFilterRows';
import { useMyProductsSavedViews } from '~/pages/my-products/lib/useMyProductsSavedViews';
import { MyProductsToolbar } from './MyProductsToolbar';
import { PageLoadingSpinner } from '~/components/ui/spinners/PageLoadingSpinner';
import {
  MAX_MARGIN_PERCENT,
  MAX_PRICE,
  MIN_MARGIN_PERCENT,
  MIN_PRICE,
} from '~/utils/moneyBounds';
import {
  extractGraphQLErrorMessages,
  formatCurrency,
  parseMarginPercentInput,
  parseOptionalPrice,
  roundTo6dp,
  type ClientError,
} from '~/utils';
import type {
  SellerCatalogPriceFiltersInput,
  SellerCatalogPricesQuery,
  SellerProductFiltersInput,
  SellerProductPriceInput,
} from '~/graphql/graphql';

const PAGE_SIZE = 25;

type CatalogPrice = NonNullable<
  SellerCatalogPricesQuery['sellerCatalogPrices']['content'][0]
>;

export function PricingTab() {
  const { t } = useTranslation('my-product');
  const { t: tGeneral } = useTranslation('general');
  const queryClient = useQueryClient();
  const [buyerMarginsFor, setBuyerMarginsFor] = useState<{
    lengthId: string;
    priceLabel: string;
  } | null>(null);

  const { data: preferencesData } = useGetUserPreferences();
  const { mutate: updatePreferences } = useUpdateUserPreferences();
  const pricingViews = useMyProductsSavedViews('PRICING');
  const [searchTerm, setSearchTerm] = useState('');
  const [searchClause, setSearchClause] = useState<SellerProductFiltersInput>();
  const panelClauses = useMemo(
    () =>
      filterRowsToClauses(
        withoutCategory(pricingViews.rows),
        categoryOf(pricingViews.rows),
        MY_PRODUCTS_FILTER_VIEWS.PRICING.fields
      ),
    [pricingViews.rows]
  );
  const debouncedSetSearchClause = useDebounce(
    (term: string) => setSearchClause(searchTermClause(term)),
    500
  );
  const filters: SellerCatalogPriceFiltersInput = useMemo(
    () =>
      buildAndFilter(
        searchClause ? [...panelClauses, searchClause] : panelClauses
      ),
    [panelClauses, searchClause]
  );
  const hasFilters = filters.and != null;

  const handleSearchChange = (value: string) => {
    setSearchTerm(value);
    debouncedSetSearchClause(value);
  };

  const clearFilters = () => {
    pricingViews.clearActiveView();
    debouncedSetSearchClause.cancel();
    setSearchTerm('');
    setSearchClause(undefined);
  };

  const {
    data,
    isLoading,
    isLoadingMore,
    sentinelRef,
    hasMoreData,
    updateItem,
  } = usePaginatedQuery<
    SellerCatalogPricesQuery,
    CatalogPrice,
    SellerCatalogPriceFiltersInput
  >({
    fetchHook: useGetSellerCatalogPrices,
    select: (data) => data?.sellerCatalogPrices,
    options: { pageSize: PAGE_SIZE, filters },
  });

  const { mutate: setSellerProductPrice } = useSetSellerProductPrice();

  const tableEditErrorHandler = (error: ClientError) =>
    toast.error(extractGraphQLErrorMessages(error).join(', '));

  const applyTableEdit = async (
    rowId: string,
    patch: (item: CatalogPrice) => CatalogPrice,
    toastMessage?: string
  ) => {
    await queryClient.cancelQueries({ queryKey: ['sellerCatalogPrices'] });
    updateItem(rowId, patch);
    toast.success(toastMessage ?? t('messages.product_updated_successfully'));
    tableControls.handleEditDone();
  };

  const submitPrice = (
    rowId: string,
    input: SellerProductPriceInput,
    toastMessage?: string
  ) => {
    setSellerProductPrice(
      { input },
      {
        onSuccess: (response) =>
          void applyTableEdit(
            rowId,
            (item) => ({ ...item, myPrice: response.setSellerProductPrice }),
            toastMessage
          ),
        onError: (error) => {
          tableEditErrorHandler(error as ClientError);
          tableControls.handleEditDone();
        },
      }
    );
  };

  const tableControls = useTable({
    onEditSubmit: (rowId, columnKey, newValue) => {
      const entry = data.find((entry) => entry?.id === rowId);
      if (!entry) return;
      const platformProductLengthId = entry.id;

      if (columnKey === 'materialCost') {
        const rawMaterialCost = String(newValue ?? '').trim();
        const materialCost = parseOptionalPrice(rawMaterialCost);
        const isClear = rawMaterialCost === '';
        submitPrice(
          rowId,
          { platformProductLengthId, materialCost, clearPrice: isClear },
          isClear ? t('messages.pricing.price_cleared') : undefined
        );
        return;
      }

      if (columnKey === 'margin') {
        const margin = parseMarginPercentInput(String(newValue ?? ''));
        submitPrice(rowId, { platformProductLengthId, margin });
      }
    },
  });

  const { order, handleColumnReorderEnd } = useColumnOrderPreference(
    preferencesData?.userPreferences?.sellerPricingColumnOrder,
    (newOrder, { onError, onSuccess }) =>
      updatePreferences(
        { preferences: { sellerPricingColumnOrder: newOrder } },
        {
          onSuccess,
          onError: () => {
            onError();
            toast.error(tGeneral('table.reorder_save_error'));
          },
        }
      )
  );

  const columns: TableColumn[] = applyColumnOrder(
    [
      { key: 'product', header: t('pricing.table.product'), width: 12 },
      { key: 'length', header: t('pricing.table.length'), width: 6 },
      {
        key: 'materialCost',
        header: t('pricing.table.material_cost'),
        isEditable: true,
        editableType: 'number',
        editableMin: MIN_PRICE,
        editableMax: MAX_PRICE,
        justification: 'end',
      },
      {
        key: 'margin',
        header: t('pricing.table.margin'),
        isEditable: true,
        editableType: 'number',
        editableMin: MIN_MARGIN_PERCENT,
        editableMax: MAX_MARGIN_PERCENT,
        justification: 'end',
      },
      {
        key: 'price',
        header: t('pricing.table.price'),
        justification: 'end',
      },
      { key: 'actions', header: '', stickTo: 'right' },
    ],
    order
  );

  const rows: TableRow[] = data.map((entry) => {
    const price = entry.myPrice;
    const currency = price?.currencyCode ?? '';
    const priceLabel = price?.pricePerUnit
      ? `${formatCurrency(price.pricePerUnit.amount, price.pricePerUnit.currency)} / ${price.unit}`
      : t('pricing.table.price');

    return {
      id: { value: entry.id },
      product: { value: entry.platformProduct.name },
      length: { value: entry.lengthDisplayValue },
      materialCost: {
        value: price?.materialCost
          ? formatCurrency(price.materialCost.amount, currency)
          : t('current_products.status.na'),
        editValue: price?.materialCost?.amount ?? '',
      },
      margin: {
        value:
          price?.margin != null
            ? `${roundTo6dp(price.margin * 100)}%`
            : t('current_products.status.na'),
        editValue: price?.margin != null ? roundTo6dp(price.margin * 100) : '',
      },
      price: {
        value: price?.pricePerUnit
          ? formatCurrency(price.pricePerUnit.amount, currency)
          : t('current_products.status.na'),
      },
      actions: {
        value: (
          <Button
            size="sm"
            width="auto"
            variant="secondary"
            type="button"
            disabled={!price}
            onClick={() =>
              setBuyerMarginsFor({ lengthId: entry.id, priceLabel })
            }
          >
            {t('pricing.buyer_margins.manage')}
          </Button>
        ),
      },
    };
  });

  return (
    <div className="flex flex-col gap-4">
      <BuyerMarginsModal
        isOpen={buyerMarginsFor != null}
        onClose={() => setBuyerMarginsFor(null)}
        platformProductLengthId={buyerMarginsFor?.lengthId}
        defaultPriceLabel={buyerMarginsFor?.priceLabel ?? ''}
      />
      <div className="w-full grid grid-cols-1 sm:grid-cols-[1fr_minmax(0,auto)] gap-4 items-end">
        <MyProductsToolbar
          view="PRICING"
          views={pricingViews}
          searchTerm={searchTerm}
          onSearchChange={handleSearchChange}
        />
      </div>
      <div className="overflow-x-auto">
        <Table
          minWidth={55}
          rows={rows}
          columns={columns}
          tableControls={tableControls}
          enableColumnReorder
          onColumnReorderEnd={handleColumnReorderEnd}
        />
        {isLoading || isLoadingMore ? (
          <PageLoadingSpinner name="products" />
        ) : null}
      </div>

      <div ref={sentinelRef} className="w-full py-2"></div>

      {!isLoading && data.length === 0 && hasFilters && (
        <div className="flex flex-col items-center gap-3 py-6 text-neut-sec">
          <p>{t('pricing.empty_filtered')}</p>
          <Button
            size="sm"
            width="auto"
            variant="secondary"
            type="button"
            onClick={clearFilters}
          >
            {t('pricing.clear_filters')}
          </Button>
        </div>
      )}

      {!isLoading && hasMoreData === false && data.length > 0 && (
        <p className="w-full text-center py-2 text-neut-sec">
          {t('current_products.no_more_products')}
        </p>
      )}
    </div>
  );
}
